import { getSizeObservable } from '@talos/kyoko';
import { motion } from 'framer-motion';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';
import { ContrastLayer, CutoutOverlayWrapper } from './styles';

export interface CutoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CutoutOverlayProps {
  /** CSS selector for the target element to highlight */
  targetSelector?: string;
  /** Pixel offset to add around the cutout (default: 8) */
  offset?: number;
  /** Border radius for the cutout corners (default: 8) */
  borderRadius?: number;
}

export function CutoutOverlay({
  children,
  targetSelector,
  offset = 8,
  borderRadius = 8,
}: PropsWithChildren<CutoutOverlayProps>) {
  const theme = useTheme();
  const [cutoutRect, setCutoutRect] = useState<CutoutRect | null>(null);
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [element, setElement] = useState<HTMLElement | null>(null);

  const updateElementRect = useCallback(
    (el: Element) => {
      const rect = el.getBoundingClientRect();
      setCutoutRect({
        x: rect.x - offset,
        y: rect.y - offset,
        width: rect.width + offset * 2,
        height: rect.height + offset * 2,
      });
    },
    [offset]
  );

  // Find and track target element
  useEffect(() => {
    const findElement = () => {
      if (targetSelector) {
        const el = document.querySelector(targetSelector) as HTMLElement | null;
        setElement(el);
        if (el) {
          updateElementRect(el);
        } else {
          setCutoutRect(null);
        }
      } else {
        setElement(null);
        setCutoutRect(null);
      }
    };

    findElement();

    // Update viewport size and element position on resize/scroll
    const handleViewportChange = () => {
      setViewportSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (targetSelector) {
        const el = document.querySelector(targetSelector);
        if (el) {
          updateElementRect(el);
        }
      }
    };

    handleViewportChange();

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [targetSelector, updateElementRect]);

  // Create size observable using getSizeObservable, recreating when element changes
  const sizeObservable = useMemo(() => {
    if (!element) {
      return null;
    }
    return getSizeObservable(element);
  }, [element]);

  // Subscribe to size changes
  useEffect(() => {
    if (!sizeObservable || !element) {
      return;
    }

    const subscription = sizeObservable.subscribe(() => {
      updateElementRect(element);
    });

    return () => subscription.unsubscribe();
  }, [sizeObservable, element, updateElementRect]);

  if (!targetSelector || !cutoutRect || viewportSize.width === 0) {
    return <>{children}</>;
  }

  return (
    <>
      <ContrastLayer
        initial={{ opacity: 0 }}
        animate={{
          opacity: 1,
          x: cutoutRect.x,
          y: cutoutRect.y,
          width: cutoutRect.width,
          height: cutoutRect.height,
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ borderRadius }}
      />
      <CutoutOverlayWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <mask id="cutout-mask">
              {/* White => visible area */}
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {/* Black cutout => transparent area */}
              <motion.rect
                initial={{
                  x: cutoutRect.x,
                  y: cutoutRect.y,
                  width: cutoutRect.width,
                  height: cutoutRect.height,
                }}
                animate={{
                  x: cutoutRect.x,
                  y: cutoutRect.y,
                  width: cutoutRect.width,
                  height: cutoutRect.height,
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                rx={borderRadius}
                ry={borderRadius}
                fill="black"
              />
            </mask>
          </defs>
          {/* Overlay with cutout */}
          <rect x="0" y="0" width="100%" height="100%" fill={theme.backgroundOverlay} mask="url(#cutout-mask)" />
        </svg>
      </CutoutOverlayWrapper>
      {children}
    </>
  );
}
